import InputAdornment from '@mui/material/InputAdornment';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from 'src/common/components/icons/Icon';
import MaterialIcon from 'src/common/components/icons/MaterialIcon';
import IconPickerTriggerButton from 'src/common/components/form/IconPickerTriggerButton';
import { isAppIconId } from 'src/common/components/icons/iconPicker';
import { materialIconIdFromAttr } from 'src/common/components/icons/materialIconRegistry';
import IconSelectorModal from 'src/common/components/modals/IconSelectorModal';

interface IconPickerFieldProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  value: string;
  onChange: (iconId: string) => void;
}

export default function IconPickerField({
  value,
  onChange,
  label,
  margin = 'normal',
  ...textFieldProps
}: IconPickerFieldProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const openDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const previewIcon = isAppIconId(value) ? value : null;
  const previewMaterialIconId = materialIconIdFromAttr(value);

  return (
    <>
      <TextField
        fullWidth
        margin={margin}
        label={label ?? t('system.pages.menuIcon')}
        value={value}
        onClick={openDialog}
        slotProps={{
          input: {
            readOnly: true,
            sx: { cursor: 'pointer' },
            startAdornment: previewIcon || previewMaterialIconId ? (
              <InputAdornment position="start">
                {previewMaterialIconId ? (
                  <MaterialIcon iconId={previewMaterialIconId} sx={{ fontSize: '1.5rem' }} />
                ) : (
                  <Icon icon={previewIcon!} sx={{ fontSize: '1.5rem' }} />
                )}
              </InputAdornment>
            ) : undefined,
            endAdornment: (
              <InputAdornment position="end">
                <IconPickerTriggerButton
                  edge="end"
                  variant="choose"
                  stopPropagation
                  onOpen={openDialog}
                  aria-label={t('editor.iconSelector.choose')}
                />
              </InputAdornment>
            ),
          },
        }}
        {...textFieldProps}
      />

      <IconSelectorModal
        mode="appIcon"
        open={open}
        onClose={handleClose}
        value={value}
        title={typeof label === 'string' ? label : t('system.pages.menuIcon')}
        confirmLabel={t('editor.iconSelector.select')}
        previewColor="primary"
        previewSize="large"
        onSelect={iconId => {
          if (iconId) {
            onChange(iconId);
          }
          setOpen(false);
        }}
      />
    </>
  );
}
